import { useEffect, useState } from 'react';
import classes from './AddToHomeScreen.module.scss';
import InstallInstructionsModal from './InstallInstructionsModal';

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isInStandaloneMode = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (window.navigator as any).standalone === true;

// Persist a timestamp of the last time we observed the PWA running
// standalone (or the browser firing `appinstalled`). We use a TTL rather
// than a permanent flag because the previous "installed once, hidden
// forever" behaviour meant users who uninstalled the app never saw the
// install banner again.
const INSTALLED_AT_KEY = 'strand.pwaInstalledAt';
const DISMISSED_AT_KEY = 'strand.pwaInstallDismissedAt';
// Legacy keys carried over from the pre-Strand brand — read once, migrate,
// then leave alone. Kept here (rather than deleted outright) so anyone who
// installed the PWA before the rebrand doesn't get re-prompted.
const LEGACY_KEYS = {
  installedAt: 'tt.pwaInstalledAt',
  installedFlag: 'tt.pwaInstalled',
  dismissedAt: 'tt.pwaInstallDismissedAt',
};
const INSTALL_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

// User-dismissed banner is silenced for the same 30-day window so they
// aren't nagged. Uninstalling the PWA later still re-shows it once the
// window elapses.
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const wasDismissedRecently = () => {
  try {
    let raw = localStorage.getItem(DISMISSED_AT_KEY);
    if (!raw) {
      const legacy = localStorage.getItem(LEGACY_KEYS.dismissedAt);
      if (legacy) {
        localStorage.setItem(DISMISSED_AT_KEY, legacy);
        localStorage.removeItem(LEGACY_KEYS.dismissedAt);
        raw = legacy;
      }
    }
    if (!raw) return false;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 && Date.now() - n < DISMISS_TTL_MS;
  } catch { return false; }
};
const markDismissed = () => {
  try { localStorage.setItem(DISMISSED_AT_KEY, String(Date.now())); } catch { /* private mode */ }
};
const readInstalledAt = (): number | null => {
  try {
    const raw = localStorage.getItem(INSTALLED_AT_KEY);
    if (raw) {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) return n;
    }
    // Migrate the pre-rebrand keys so already-installed users don't get
    // re-prompted. Handles both the timestamp variant and the older boolean.
    const legacyAt = localStorage.getItem(LEGACY_KEYS.installedAt);
    if (legacyAt) {
      const n = Number(legacyAt);
      if (Number.isFinite(n) && n > 0) {
        localStorage.setItem(INSTALLED_AT_KEY, String(n));
        localStorage.removeItem(LEGACY_KEYS.installedAt);
        return n;
      }
    }
    if (localStorage.getItem(LEGACY_KEYS.installedFlag) === '1') {
      const now = Date.now();
      localStorage.setItem(INSTALLED_AT_KEY, String(now));
      localStorage.removeItem(LEGACY_KEYS.installedFlag);
      return now;
    }
    return null;
  } catch { return null; }
};
const wasInstalledRecently = () => {
  const at = readInstalledAt();
  return at !== null && Date.now() - at < INSTALL_TTL_MS;
};
const markInstalled = () => {
  try { localStorage.setItem(INSTALLED_AT_KEY, String(Date.now())); } catch { /* private mode */ }
};

// The "enable push" prompt is handled by EnablePushChip in the floating
// top-right actions cluster. This bottom banner is only for install nudges
// on browsers that either fire beforeinstallprompt (Chromium) or need the
// manual "how to install" flow (Firefox / Samsung / etc.).
type Mode = 'install-android' | 'install-manual' | null;

interface AddToHomeScreenProps {
  // When mounted inside the app shell we need to clear the floating
  // BottomNav pill; on the standalone Landing page the banner can sit
  // flush with the bottom edge.
  variant?: 'flush' | 'floating';
}

const AddToHomeScreen: React.FC<AddToHomeScreenProps> = ({ variant = 'flush' }) => {
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const [manualFallbackReady, setManualFallbackReady] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [installed, setInstalled] = useState(() => wasInstalledRecently());
  const [dismissed, setDismissed] = useState(() => wasDismissedRecently());

  const standalone = isInStandaloneMode();
  const ios = isIOS();

  // Any current standalone session means the PWA is installed on this device.
  useEffect(() => {
    if (standalone && !installed) {
      markInstalled();
      setInstalled(true);
    }
  }, [standalone, installed]);

  // Always swallow beforeinstallprompt when the app is already installed —
  // Chrome will occasionally re-fire it (e.g. if the browser thinks the
  // manifest identity changed) and we don't want the mini-infobar or a
  // duplicate "install this" prompt showing up.
  useEffect(() => {
    if (!standalone && !installed) return;
    const swallow = (e: Event) => e.preventDefault();
    window.addEventListener('beforeinstallprompt', swallow as EventListener);
    return () => window.removeEventListener('beforeinstallprompt', swallow as EventListener);
  }, [standalone, installed]);

  useEffect(() => {
    if (standalone || ios || installed) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler as EventListener);

    const installedHandler = () => {
      markInstalled();
      setInstalled(true);
      setInstallPrompt(null);
    };
    window.addEventListener('appinstalled', installedHandler);

    // Non-Chromium browsers never fire beforeinstallprompt. Fall back to the
    // "how to install" flow after a short wait so those users see something.
    const t = setTimeout(() => setManualFallbackReady(true), 2500);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler as EventListener);
      window.removeEventListener('appinstalled', installedHandler);
      clearTimeout(t);
    };
  }, [standalone, ios, installed]);

  const mode: Mode = (() => {
    if (standalone || ios || installed || dismissed) return null;
    if (installPrompt) return 'install-android';
    if (manualFallbackReady) return 'install-manual';
    return null;
  })();

  const handleDismiss = () => {
    markDismissed();
    setDismissed(true);
  };

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice?.outcome === 'accepted') {
      markInstalled();
      setInstalled(true);
    }
    setInstallPrompt(null);
  };

  const openHowTo = () => setModalOpen(true);

  if (!mode) return modalOpen ? <InstallInstructionsModal onClose={() => setModalOpen(false)} /> : null;

  const bannerClass = variant === 'floating'
    ? `${classes.banner} ${classes['banner--floating']}`
    : classes.banner;

  return (
    <>
      <div className={bannerClass}>
        <div className={classes.banner__inner}>
          <span className={classes.banner__text}>
            Add Strand Services to your home screen
          </span>
          {mode === 'install-android' ? (
            <button type="button" className={classes.banner__install} onClick={handleInstall}>
              Add
            </button>
          ) : (
            <button type="button" className={classes.banner__install} onClick={openHowTo}>
              Show me how
            </button>
          )}
          <button
            type="button"
            className={classes.banner__dismiss}
            onClick={handleDismiss}
            aria-label="Dismiss install prompt"
            title="Not now"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
      {modalOpen && <InstallInstructionsModal onClose={() => setModalOpen(false)} />}
    </>
  );
};

export default AddToHomeScreen;
